import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  DateInput,
  EmptyState,
  FieldBlock,
  Input,
  LoadingState,
  Modal,
  Select,
  TagSelect,
  Textarea,
  useToast,
  type SelectOption,
} from '@orbind/ui'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import { api } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { usePreferenciaDeLayout } from '../lib/layout-prefs.ts'
import { abrirNoNavegadorPadrao } from '../lib/link-externo.ts'
import { useSession } from '../lib/session.tsx'
import { initialsOf } from '../features/work-item/bits.tsx'
import { Chip, GradeDeHoras } from '../features/calendar/GradeDeHoras.tsx'
import { CriacaoRapida } from '../features/calendar/CriacaoRapida.tsx'
import { DetalheDoCompromisso } from '../features/calendar/DetalheDoCompromisso.tsx'
import { MiniCalendario } from '../features/calendar/MiniCalendario.tsx'
import { Popover, ancoraDoClique, ancoraNoCentro, ancoraNoPonto } from '../features/calendar/Popover.tsx'
import {
  AGENDA_IDS,
  AGENDAS,
  AGENDAS_CRIAVEIS,
  agendaInfo,
  chipsQueCabem,
  corpoDoForm,
  dayKey,
  dicaDasPessoas,
  diasDoPeriodo,
  ehDiaValido,
  errosDoForm,
  eventoParaCompromisso,
  faixaDeHorario,
  filtrarCompromissos,
  formDoEvento,
  formVazio,
  gradeDoMes,
  indexarPorDia,
  intersectaPeriodo,
  montarCompromissos,
  navegar,
  parseDayKey,
  periodoDaContagem,
  periodoVisivel,
  somarDias,
  type AgendaDeEvento,
  type AgendaId,
  type Ancora,
  type Audiencia,
  type Compromisso,
  type CorpoDoEvento,
  type EventoDaApi,
  type Exibicao,
  type FormDeEvento,
} from '../features/calendar/calendario.ts'
import '../features/work-item/wi.css'
import './calendar.css'

/* ------------------------------------------------------------------ datas */

const MONTH_FMT = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
const MES_CURTO_FMT = new Intl.DateTimeFormat('pt-BR', { month: 'short' })
const DIA_TITULO_FMT = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
const DAY_LONG_FMT = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const DOW_FMT = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })

/** Painel do dia (à direita): largura ajustável pela pessoa, guardada na conta. */
const LARGURA_DO_DIA_PADRAO = 340
const LARGURA_DO_DIA_MIN = 260
const LARGURA_DO_DIA_MAX = 720
/** O centro (mês/semana/dia) nunca fica mais estreito que isto ao arrastar. */
const CENTRO_MIN = 420
/** Largura do divisor (`.cal__divisor` em calendar.css). */
const LARGURA_DO_DIVISOR = 18
const PASSO_DO_TECLADO = 20

/** Cabeçalho da grade do mês: DOM, SEG… derivado do Intl a partir de uma semana de referência. */
const WEEKDAYS = Array.from({ length: 7 }, (_, index) =>
  DOW_FMT.format(new Date(2026, 7, 2 + index)).replace('.', '').toUpperCase(),
)

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

const plural = (n: number) => `${n} ${n === 1 ? 'compromisso' : 'compromissos'}`

const mesCurto = (d: Date) => MES_CURTO_FMT.format(d).replace('.', '')

/**
 * Título da barra, como no design: "28 de outubro de 2026" no Dia; o mês na
 * Semana ("Out – nov de 2026" quando a semana cruza meses), no Mês e na Agenda.
 */
function tituloDoPeriodo(display: Exibicao, selected: string): string {
  const dia = parseDayKey(selected)
  if (display === 'day') return DIA_TITULO_FMT.format(dia)
  if (display === 'week') {
    const periodo = periodoVisivel('week', selected)
    const de = parseDayKey(periodo.de)
    const ate = parseDayKey(periodo.ate)
    if (de.getMonth() === ate.getMonth()) return capitalize(MONTH_FMT.format(de))
    return `${capitalize(mesCurto(de))} – ${mesCurto(ate)} de ${ate.getFullYear()}`
  }
  return capitalize(MONTH_FMT.format(dia))
}

/** Hora da linha no painel e na Agenda: "09:00 – 10:00", "Dia inteiro" ou o intervalo de dias. */
function horarioDaLinha(c: Compromisso): string {
  if (c.endDate !== null && c.endDate > c.date) {
    const faixa = `${Number(c.date.slice(8))}/${Number(c.date.slice(5, 7))} → ${Number(c.endDate.slice(8))}/${Number(c.endDate.slice(5, 7))}`
    return c.startTime === null ? faixa : `${faixa} · ${c.startTime}`
  }
  return faixaDeHorario(c).replace(' — ', ' – ')
}

/* --------------------------------------------------------------- opções -- */

/** Ordem do design: Dia, Semana, Mês, Agenda — cada uma com o seu atalho. */
const EXIBICOES: readonly { id: Exibicao; label: string; atalho: string }[] = [
  { id: 'day', label: 'Dia', atalho: 'D' },
  { id: 'week', label: 'Semana', atalho: 'W' },
  { id: 'month', label: 'Mês', atalho: 'M' },
  { id: 'agenda', label: 'Agenda', atalho: 'A' },
]
const EXIBICAO_IDS: readonly string[] = EXIBICOES.map((e) => e.id)

const NAV_LABEL: Record<Exibicao, [string, string]> = {
  month: ['Mês anterior', 'Próximo mês'],
  week: ['Semana anterior', 'Próxima semana'],
  day: ['Dia anterior', 'Próximo dia'],
  agenda: ['Mês anterior', 'Próximo mês'],
}

/** Seção "Visão" do explorer: QUEM aparece (protótipo `_shell.html:399-405`). */
const AUDIENCE_OPTIONS: readonly { id: Audiencia; label: string; icon: string }[] = [
  { id: 'me', label: 'Somente eu', icon: 'fas fa-user' },
  { id: 'all', label: 'Todo mundo', icon: 'fas fa-users' },
  { id: 'custom', label: 'Selecionar pessoas', icon: 'fas fa-user-check' },
]

const OPCOES_DE_AGENDA: readonly SelectOption<AgendaDeEvento>[] = AGENDAS_CRIAVEIS.map((a) => ({
  value: a.id,
  label: a.label,
  icon: a.icon,
}))

/**
 * Formulário aberto: novo (`id` nulo) ou edição; `original` é o corpo inicial
 * (dirty check). Com `ancora`, é a criação rápida no popover; sem, o
 * formulário completo (edição, ou "Mais opções").
 */
type Edicao = { form: FormDeEvento; id: string | null; original: string; dono: string; ancora: Ancora | null }

/** Popover aberto: o detalhe de um compromisso ou o "+N mais" de um dia do mês. */
type Pop = { tipo: 'compromisso'; c: Compromisso; ancora: Ancora } | { tipo: 'dia'; dia: string; ancora: Ancora }

const erroDe = (erro: unknown) =>
  erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.'

/** Digitando num campo? Aí as teclas são texto, não atalho. */
const digitando = (alvo: EventTarget | null) => {
  const el = alvo as HTMLElement | null
  return Boolean(el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable))
}

/* ------------------------------------------------------------------ tela  */

/**
 * Calendário com dados reais (SPEC-20261027-2350-calendario-real), no
 * desenho do design do calendário (2026-10-31): barra com Hoje, setas, o
 * período, o painel do dia e a exibição; o explorer com Criar, o mini
 * calendário, Visão, Agendas, Google Calendar e Pessoas; no centro, o cartão
 * com Dia, Semana, Mês ou Agenda; à direita, o painel do dia.
 *
 * Clicar num compromisso abre o detalhe num popover ao lado dele; clicar num
 * horário (ou duplo clique num dia do mês) abre a criação rápida ali mesmo.
 *
 * A cor de cada agenda nunca é o único sinal: todo compromisso carrega o
 * ícone e o nome da agenda junto do marcador colorido.
 */
export function CalendarPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const { search } = useExplorer()
  const session = useSession()
  const eu = session.id
  const bootstrap = useBootstrap()
  const [params, setParams] = useSearchParams()
  const dataParam = params.get('data')
  const eventoParam = params.get('evento')

  const hoje = useMemo(() => dayKey(new Date()), [])
  // `?data=AAAA-MM-DD` abre o calendário nesse dia (o aviso de convite manda)
  const [selected, setSelected] = useState(() => (ehDiaValido(dataParam) ? dataParam : hoje))

  // a exibição fica na conta (Semana é o padrão do design)
  const [exibicaoSalva, setExibicaoSalva] = usePreferenciaDeLayout<string>('calendarioExibicao', 'week')
  const display: Exibicao = EXIBICAO_IDS.includes(exibicaoSalva) ? (exibicaoSalva as Exibicao) : 'week'
  const setDisplay = (exibicao: Exibicao) => {
    setExibicaoSalva(exibicao)
    setPop(null)
  }

  useEffect(() => {
    if (ehDiaValido(dataParam)) setSelected(dataParam)
  }, [dataParam])

  const [audience, setAudience] = useState<Audiencia>('all')
  const [activeAgendas, setActiveAgendas] = useState<readonly AgendaId[]>(AGENDA_IDS)
  const [activePeople, setActivePeople] = useState<readonly string[]>(() => [eu])

  /*
   * Painel do dia: aberto ou recolhido (botão na barra e tecla "]") e a
   * largura, ajustada arrastando o divisor. O painel cresce para a ESQUERDA,
   * então a largura nova é a inicial mais o quanto o ponteiro andou para a
   * esquerda. As duas ficam na conta, como as do painel de membros.
   */
  const [painelAberto, setPainelAberto] = usePreferenciaDeLayout<boolean>('calendarioDiaAberto', true)
  const [larguraDoDia, setLarguraDoDia] = usePreferenciaDeLayout<number>('calendarioDiaLargura', LARGURA_DO_DIA_PADRAO)
  const corpoRef = useRef<HTMLDivElement>(null)
  const limitarLarguraDoDia = (px: number) => {
    const total = corpoRef.current?.clientWidth ?? Number.POSITIVE_INFINITY
    const maximo = Math.max(LARGURA_DO_DIA_MIN, Math.min(LARGURA_DO_DIA_MAX, total - CENTRO_MIN - LARGURA_DO_DIVISOR))
    return Math.round(Math.min(maximo, Math.max(LARGURA_DO_DIA_MIN, px)))
  }
  const iniciarArrasteDoDia = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    const x0 = e.clientX
    const l0 = larguraDoDia
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const mover = (ev: PointerEvent) => setLarguraDoDia(limitarLarguraDoDia(l0 + (x0 - ev.clientX)))
    const soltar = () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('pointermove', mover)
      document.removeEventListener('pointerup', soltar)
    }
    document.addEventListener('pointermove', mover)
    document.addEventListener('pointerup', soltar)
  }
  const teclaNoDivisor = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowLeft' ? PASSO_DO_TECLADO : e.key === 'ArrowRight' ? -PASSO_DO_TECLADO : 0
    if (delta === 0) return
    e.preventDefault()
    setLarguraDoDia(limitarLarguraDoDia(larguraDoDia + delta))
  }

  const [pop, setPop] = useState<Pop | null>(null)
  const [edicao, setEdicao] = useState<Edicao | null>(null)
  const [tentouSalvar, setTentouSalvar] = useState(false)
  const [excluindo, setExcluindo] = useState<EventoDaApi | null>(null)

  /* ---------------------------------------------------------------- dados */

  const periodo = useMemo(() => periodoVisivel(display, selected), [display, selected])

  // o período visível é a chave: navegar pede só o que a tela mostra; o
  // anterior fica na tela até o novo chegar (sem piscar o skeleton)
  // "Sincronizar agora": a próxima leitura pede ao servidor para não usar o cache do Google
  const atualizarGoogle = useRef(false)
  const consulta = useQuery({
    queryKey: ['calendar', periodo.de, periodo.ate],
    queryFn: async () => {
      const forcar = atualizarGoogle.current
      atualizarGoogle.current = false
      const query = { de: periodo.de, ate: periodo.ate, ...(forcar ? { google: 'atualizar' as const } : {}) }
      return (await api.calendar.events.$get({ query })).json()
    },
    placeholderData: keepPreviousData,
  })

  const membros = useMemo(() => bootstrap.data?.members ?? [], [bootstrap.data])
  const membroPorId = useMemo(() => new Map(membros.map((m) => [m.id, m])), [membros])
  const nomeDe = (id: string) => {
    if (id === eu) return `${membroPorId.get(id)?.name ?? session.name} (você)`
    return membroPorId.get(id)?.name ?? 'Pessoa removida'
  }

  /** Pessoas ativas do workspace, comigo primeiro. */
  const pessoas = useMemo(() => {
    const ativas = membros.filter((m) => m.isActive)
    return [...ativas.filter((m) => m.id === eu), ...ativas.filter((m) => m.id !== eu)]
  }, [membros, eu])

  /*
    Google Calendar da pessoa (SPEC-20261027-2350-integracao-google-calendar). A conexão abre
    numa aba nova (no desktop, no navegador do sistema); enquanto isso a tela
    pergunta de 3 em 3 segundos se já conectou, e ao voltar o foco também.
  */
  const [aguardandoGoogle, setAguardandoGoogle] = useState(false)
  const google = useQuery({
    queryKey: ['calendar-google'],
    queryFn: async () => (await api.calendar.google.$get()).json(),
    refetchOnWindowFocus: true,
    refetchInterval: aguardandoGoogle ? 3000 : false,
  })
  const googleConectado = google.data?.conectado === true

  useEffect(() => {
    // "Conectar de novo": só conta quando o erro some (a conexão nova zera o erro)
    if (!aguardandoGoogle || !googleConectado || google.data?.erro) return
    setAguardandoGoogle(false)
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    toast.push({ tone: 'success', message: 'Google Calendar conectado.' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aguardandoGoogle, googleConectado, google.data?.erro])

  // a página de retorno do Google traz de volta com ?google=conectado
  useEffect(() => {
    if (params.get('google') !== 'conectado') return
    void google.refetch()
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    const sem = new URLSearchParams(params)
    sem.delete('google')
    setParams(sem, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  const conectarGoogle = useMutation({
    mutationFn: async () => {
      const resposta = await api.calendar.google.conectar.$post()
      return (await resposta.json()) as { url: string }
    },
    onSuccess: ({ url }) => {
      setAguardandoGoogle(true)
      void abrirNoNavegadorPadrao(url)
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const desconectarGoogle = useMutation({
    mutationFn: async () => {
      await api.calendar.google.$delete()
    },
    onSuccess: () => {
      void google.refetch()
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      toast.push({ tone: 'success', message: 'Google Calendar desconectado. Os eventos já enviados continuam no Google.' })
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const sincronizarGoogle = () => {
    atualizarGoogle.current = true
    void consulta.refetch().then(() => google.refetch())
  }

  const compromissos = useMemo(() => (consulta.data ? montarCompromissos(consulta.data, eu) : []), [consulta.data, eu])

  const termo = search.trim()
  const filtros = { audiencia: audience, eu, pessoas: activePeople, agendas: activeAgendas, termo }

  /** Compromissos que sobrevivem à agenda, à "Visão", às pessoas e à busca. */
  const visiveis = useMemo(
    () => filtrarCompromissos(compromissos, filtros),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compromissos, audience, eu, activePeople, activeAgendas, termo],
  )

  /** Índice dia → compromissos: evento de vários dias entra em cada dia. */
  const porDia = useMemo(() => indexarPorDia(visiveis, periodo.de, periodo.ate), [visiveis, periodo])

  /**
   * "Meus compromissos" (SPEC-20261104-1428): o painel do dia mostra só o que é
   * de QUEM está logado — dono ou participante —, sem passar pelos filtros da
   * tela (visão, pessoas, agendas, busca). Só o dia selecionado.
   */
  const porDiaMeus = useMemo(() => {
    const meus = compromissos.filter((c) => c.ownerMemberId === eu || c.memberIds.includes(eu))
    return indexarPorDia(meus, selected, selected)
  }, [compromissos, eu, selected])
  const meusNoDia = porDiaMeus.get(selected) ?? []

  const contagem = periodoDaContagem(display, selected)
  const noPeriodo = visiveis.filter((c) => intersectaPeriodo(c, contagem.de, contagem.ate))

  /** Contagem por agenda no explorer — independe da agenda estar ligada. */
  const porAgenda = useMemo(() => {
    const contas = new Map<AgendaId, number>()
    for (const c of filtrarCompromissos(compromissos, { ...filtros, agendas: AGENDA_IDS })) {
      if (intersectaPeriodo(c, contagem.de, contagem.ate)) contas.set(c.agenda, (contas.get(c.agenda) ?? 0) + 1)
    }
    return contas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compromissos, audience, eu, activePeople, termo, contagem.de, contagem.ate])

  /* ------------------------------------------------------------- filtros */

  const toggleAgenda = (id: AgendaId) =>
    setActiveAgendas((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))

  /** Agendas na barra: a do Google só aparece com a conta conectada. */
  const agendasNaBarra = AGENDAS.filter((agenda) => agenda.id !== 'google' || googleConectado)
  const agendasLigadas = agendasNaBarra.filter((agenda) => activeAgendas.includes(agenda.id)).length

  /**
   * Ligar/desligar uma pessoa implica "Selecionar pessoas": é o único modo em
   * que a escolha individual tem efeito (protótipo `_shell.html:3444-3448`).
   */
  const togglePerson = (id: string) => {
    if (audience !== 'custom') {
      setAudience('custom')
      setActivePeople(audience === 'all' ? pessoas.map((p) => p.id).filter((p) => p !== id) : [id])
      return
    }
    setActivePeople((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  /** "Marcar todos" volta a "Todo mundo"; "Desmarcar todos" deixa "Selecionar pessoas" sem ninguém. */
  const marcarTodasAsPessoas = () => {
    setAudience('all')
    setActivePeople(pessoas.map((p) => p.id))
  }
  const desmarcarTodasAsPessoas = () => {
    setAudience('custom')
    setActivePeople([])
  }

  const filtrosTocados = audience !== 'all' || activeAgendas.length !== AGENDA_IDS.length

  const resetFilters = () => {
    setAudience('all')
    setActiveAgendas(AGENDA_IDS)
    setActivePeople([eu])
  }

  /* ------------------------------------------------------------ navegação */

  const irPara = (delta: -1 | 1) => {
    setSelected(navegar(display, selected, delta, hoje))
    setPop(null)
  }
  // "Hoje" (e a tecla T) também leva a vista até hoje quando o período já é o de hoje
  const [foco, setFoco] = useState(0)
  // "Hoje" do painel do dia (SPEC-20261104-1428): rola a grade até agora
  const [focoDoDia, setFocoDoDia] = useState(0)
  const irParaHoje = () => {
    setSelected(hoje)
    setPop(null)
    setFoco((n) => n + 1)
  }
  const verDia = (dia: string) => {
    setSelected(dia)
    setDisplay('day')
  }

  /* ------------------------------------------------------------- eventos */

  /** Todo compromisso abre o detalhe ao lado de onde foi clicado. */
  const abrirCompromisso = (c: Compromisso, ancora: Ancora) => setPop({ tipo: 'compromisso', c, ancora })

  /*
   * `?evento=<id>` (aviso de convite): abre o evento quando ele estiver
   * carregado. Fora do período carregado, busca o evento sozinho.
   */
  const listaPronta = consulta.data !== undefined && !consulta.isPlaceholderData
  const eventoNaLista = eventoParam ? consulta.data?.eventos.find((e) => e.id === eventoParam) : undefined
  const eventoAvulso = useQuery({
    queryKey: ['calendar', 'evento', eventoParam],
    queryFn: async () => (await api.calendar.events[':id'].$get({ param: { id: eventoParam! } })).json(),
    enabled: Boolean(eventoParam) && listaPronta && !eventoNaLista,
    retry: false,
  })

  useEffect(() => {
    if (!eventoParam) return
    const consumir = () =>
      setParams(
        (atual) => {
          const proximo = new URLSearchParams(atual)
          proximo.delete('evento')
          proximo.delete('data')
          return proximo
        },
        { replace: true },
      )
    if (eventoNaLista) {
      setPop({ tipo: 'compromisso', c: eventoParaCompromisso(eventoNaLista), ancora: ancoraNoCentro() })
      setSelected(eventoNaLista.date)
      consumir()
      return
    }
    const avulso = eventoAvulso.data?.evento
    if (avulso) {
      const ev: EventoDaApi = {
        id: avulso.id,
        agenda: avulso.agenda,
        title: avulso.title,
        description: avulso.description,
        location: avulso.location,
        meetingUrl: avulso.meetingUrl,
        date: avulso.date,
        endDate: avulso.endDate,
        startTime: avulso.startTime,
        endTime: avulso.endTime,
        ownerMemberId: avulso.ownerMemberId,
        ownerName: membroPorId.get(avulso.ownerMemberId)?.name ?? '—',
        memberIds: avulso.memberIds,
        podeEditar: avulso.podeEditar,
      }
      setPop({ tipo: 'compromisso', c: eventoParaCompromisso(ev), ancora: ancoraNoCentro() })
      setSelected(avulso.date)
      consumir()
      return
    }
    if (eventoAvulso.isError) {
      toast.push({ tone: 'warning', message: 'Este evento não existe mais ou você não tem acesso a ele.' })
      consumir()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoParam, eventoNaLista, eventoAvulso.data, eventoAvulso.isError])

  /* ------------------------------------------------------ formulário ---- */

  /** Criação rápida no popover (ou, sem âncora, no centro da tela). */
  const abrirCriacao = (dia: string = selected, hora: string | null = null, ancora: Ancora = ancoraNoCentro()) => {
    // conectado ao Google, o evento novo já vem marcado para ir para lá
    setEdicao({
      form: { ...formVazio(parseDayKey(dia), hora), googleSync: googleConectado },
      id: null,
      original: '',
      dono: eu,
      ancora,
    })
    setTentouSalvar(false)
    setPop(null)
  }

  const abrirEdicao = (ev: EventoDaApi) => {
    const form = formDoEvento(ev)
    setEdicao({ form, id: ev.id, original: JSON.stringify(corpoDoForm(form)), dono: ev.ownerMemberId, ancora: null })
    setTentouSalvar(false)
    setPop(null)
  }

  const atualizarForm = (patch: Partial<FormDeEvento>) =>
    setEdicao((atual) => (atual ? { ...atual, form: { ...atual.form, ...patch } } : atual))

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['calendar'] })

  const salvar = useMutation({
    mutationFn: async ({ id, corpo }: { id: string | null; corpo: CorpoDoEvento }) => {
      if (id) await api.calendar.events[':id'].$put({ param: { id }, json: corpo })
      else await api.calendar.events.$post({ json: corpo })
    },
    onSuccess: (_, { id, corpo }) => {
      invalidar()
      setEdicao(null)
      setSelected(corpo.date)
      // a agenda do evento novo volta a aparecer, se estava escondida
      setActiveAgendas((atuais) => (atuais.includes(corpo.agenda) ? atuais : [...atuais, corpo.agenda]))
      toast.push({
        tone: 'success',
        message: id
          ? 'Evento atualizado.'
          : corpo.memberIds.length > 0
            ? 'Evento criado. Os convidados foram avisados.'
            : `Evento salvo em ${agendaInfo(corpo.agenda).label}.`,
      })
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.calendar.events[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setExcluindo(null)
      toast.push({ tone: 'success', message: 'Evento excluído.' })
    },
    onError: (erro) => {
      setExcluindo(null)
      toast.push({ tone: 'danger', message: erroDe(erro) })
    },
  })

  const erros = edicao ? errosDoForm(edicao.form) : {}
  const sujo = edicao ? edicao.id === null || JSON.stringify(corpoDoForm(edicao.form)) !== edicao.original : false

  const salvarEvento = () => {
    if (!edicao) return
    setTentouSalvar(true)
    if (Object.keys(erros).length > 0) return
    salvar.mutate({ id: edicao.id, corpo: corpoDoForm(edicao.form) })
  }

  /** Convidados possíveis: as pessoas ativas, menos o dono (quem convidado já é inativo continua). */
  const opcoesDeConvidado: SelectOption[] = edicao
    ? [
        ...pessoas.filter((m) => m.id !== edicao.dono).map((m) => ({ value: m.id, label: m.name })),
        ...edicao.form.memberIds
          .filter((id) => id !== edicao.dono && !pessoas.some((p) => p.id === id))
          .map((id) => ({ value: id, label: `${membroPorId.get(id)?.name ?? 'Pessoa removida'} (inativa)` })),
      ]
    : []

  // "Criar" mora no explorer (design): o "+" do topo do explorer sai
  useExplorerConfig({ footer: 'Minha agenda, Corporativo, Datas comemorativas e Itens de Trabalho' })

  /* ------------------------------------------------------------ atalhos */

  /*
   * Atalhos do design: T hoje; D/W(S)/M/A exibição; J/N/→ e K/P/← navegam;
   * ] painel do dia; C cria; / busca. Ficam quietos com um campo em foco, com
   * Ctrl/⌘/Alt e com um formulário ou popover aberto (lá o Esc fecha).
   */
  const atalhos = useRef<(e: KeyboardEvent) => void>(() => undefined)
  atalhos.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || digitando(e.target)) return
    if (edicao || pop || excluindo) return
    const tecla = e.key.length === 1 ? e.key.toLowerCase() : e.key
    const acao: Record<string, () => void> = {
      t: irParaHoje,
      d: () => setDisplay('day'),
      w: () => setDisplay('week'),
      s: () => setDisplay('week'),
      m: () => setDisplay('month'),
      a: () => setDisplay('agenda'),
      j: () => irPara(1),
      n: () => irPara(1),
      ArrowRight: () => irPara(1),
      k: () => irPara(-1),
      p: () => irPara(-1),
      ArrowLeft: () => irPara(-1),
      ']': () => setPainelAberto(!painelAberto),
      c: () => abrirCriacao(),
      '/': () => document.querySelector<HTMLInputElement>('.shell__explorer-search input')?.focus(),
    }
    const fazer = acao[tecla]
    if (!fazer) return
    e.preventDefault()
    fazer()
  }
  useEffect(() => {
    const ouvir = (e: KeyboardEvent) => atalhos.current(e)
    window.addEventListener('keydown', ouvir)
    return () => window.removeEventListener('keydown', ouvir)
  }, [])

  /* --------------------------------------- agenda: abre rolada até hoje */

  const agendaRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const lista = agendaRef.current
    const dia = lista?.querySelector<HTMLElement>('[data-hoje]')
    if (!lista || !dia) return
    lista.scrollTop += dia.getBoundingClientRect().top - lista.getBoundingClientRect().top
  }, [display, consulta.isPending, foco])

  /* -------------------------------------------- mês: quantos chips cabem */

  const gradeDoMesRef = useRef<HTMLDivElement>(null)
  const [cabem, setCabem] = useState({ todos: 3, comMais: 2 })
  useLayoutEffect(() => {
    const el = gradeDoMesRef.current
    if (!el) return
    const medir = () => {
      const proximo = chipsQueCabem(el.clientHeight / 6)
      setCabem((atual) => (atual.todos === proximo.todos && atual.comMais === proximo.comMais ? atual : proximo))
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [display, consulta.isPending])

  const marcadas = pessoas.filter((p) => activePeople.includes(p.id)).length
  const dicaPessoas = dicaDasPessoas(audience, marcadas, pessoas.length)
  const titulo = tituloDoPeriodo(display, selected)
  const selectedEvents = porDia.get(selected) ?? []
  const atualizando = consulta.isFetching && consulta.isPlaceholderData

  /* ---------------------------------------------------------- estados --- */

  const vazio = (() => {
    const tituloVazio =
      display === 'week'
        ? 'Nenhum compromisso nesta semana'
        : display === 'day'
          ? 'Nenhum compromisso neste dia'
          : `Nenhum compromisso em ${titulo.toLowerCase()}`
    const descricao =
      termo !== ''
        ? `Nenhum compromisso deste período corresponde a “${termo}”.`
        : filtrosTocados
          ? 'A “Visão”, as agendas ou as pessoas escolhidas estão escondendo os compromissos deste período.'
          : display === 'agenda'
            ? 'Crie um evento ou navegue para outro período.'
            : display === 'month'
              ? 'Dê um duplo clique num dia para criar um evento.'
              : 'Clique num horário para criar um evento.'
    const acao = filtrosTocados ? (
      <Button size="sm" variant="primary" icon="fas fa-eraser" onClick={resetFilters}>
        Limpar filtros
      </Button>
    ) : (
      <Button size="sm" variant="primary" icon="fas fa-plus" onClick={() => abrirCriacao()}>
        Criar evento
      </Button>
    )
    return { titulo: tituloVazio, descricao, acao }
  })()

  /** Nas grades o vazio é um aviso enxuto: a grade continua clicável para criar. */
  const avisoDeVazio =
    noPeriodo.length === 0 ? (
      <div className="cal__vazio" role="status">
        <i className="far fa-calendar" aria-hidden />
        <div className="cal__vaziotext">
          <strong>{vazio.titulo}</strong>
          <span>{vazio.descricao}</span>
        </div>
        {vazio.acao}
      </div>
    ) : null

  /* -------------------------------------------------------------- render */

  const renderMes = () => (
    <div className="cal-month">
      {avisoDeVazio}
      <div className="cal-month__dows" aria-hidden>
        {WEEKDAYS.map((dow) => (
          <span key={dow}>{dow}</span>
        ))}
      </div>
      <div className="cal-month__grid" ref={gradeDoMesRef} aria-label={`Grade de ${titulo}`}>
        {gradeDoMes(selected).map((key) => {
          const day = parseDayKey(key)
          const dayEvents = porDia.get(key) ?? []
          const isToday = key === hoje
          const isSelected = key === selected
          const outside = key.slice(0, 7) !== selected.slice(0, 7)
          const sobra = dayEvents.length > cabem.todos
          const mostrados = sobra ? cabem.comMais : dayEvents.length
          const resto = dayEvents.length - mostrados
          return (
            // célula é um `div`: os chips dentro dela são botões e botão
            // dentro de botão não existe em HTML válido. Clique escolhe o
            // dia (o painel ao lado mostra); duplo clique cria um evento.
            <div
              key={key}
              className={['cal-cell', outside ? 'cal-cell--outside' : '', isSelected ? 'cal-cell--sel' : '']
                .filter(Boolean)
                .join(' ')}
              title="Clique para ver o dia · duplo clique para criar evento"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('button')) return
                setSelected(key)
              }}
              onDoubleClick={(event) => {
                if ((event.target as HTMLElement).closest('button')) return
                setSelected(key)
                abrirCriacao(key, null, ancoraNoPonto(event.clientX, event.clientY))
              }}
            >
              <button
                type="button"
                className={`cal-cell__num${isToday ? ' cal-cell__num--hoje' : ''}`}
                aria-current={isToday ? 'date' : undefined}
                aria-label={`Ver ${DAY_LONG_FMT.format(day)} — ${plural(dayEvents.length)}`}
                title="Ver dia"
                onClick={() => verDia(key)}
              >
                {day.getDate() === 1 ? `${day.getDate()} ${mesCurto(day)}` : day.getDate()}
              </button>
              {dayEvents.slice(0, mostrados).map((c) => (
                <Chip key={c.id} c={c} dia={key} onAbrir={abrirCompromisso} />
              ))}
              {resto > 0 ? (
                <button
                  type="button"
                  className="cal-mais"
                  onClick={(event) => setPop({ tipo: 'dia', dia: key, ancora: ancoraDoClique(event) })}
                >
                  {mostrados > 0 ? `+${resto} mais` : `${resto} ${resto === 1 ? 'evento' : 'eventos'}`}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )

  const renderAgenda = () => {
    const grupos = diasDoPeriodo(periodo.de, periodo.ate)
      .filter((dia) => porDia.has(dia) || dia === hoje)
      .map((dia) => ({ key: dia, date: parseDayKey(dia), events: porDia.get(dia) ?? [] }))
    if (grupos.every((g) => g.events.length === 0)) {
      return (
        <div className="cal__emptymonth">
          <EmptyState icon="far fa-calendar" title={vazio.titulo} description={vazio.descricao} action={vazio.acao} />
        </div>
      )
    }
    return (
      <div className="cal-agenda" ref={agendaRef}>
        {grupos.map((group) => {
          const ehHoje = group.key === hoje
          return (
            <section key={group.key} className="cal-agenda__day" data-hoje={ehHoje || undefined}>
              <button type="button" className="cal-agenda__dayhead" title="Ver dia" onClick={() => verDia(group.key)}>
                <span className={`cal-agenda__daynum${ehHoje ? ' cal-agenda__daynum--hoje' : ''}`}>
                  {group.date.getDate()}
                </span>
                <span className={`cal-agenda__daytext${ehHoje ? ' cal-agenda__daytext--hoje' : ''}`}>
                  {mesCurto(group.date).toUpperCase()}, {WEEKDAYS[group.date.getDay()]}
                  {ehHoje ? <span className="cal-agenda__selo">Hoje</span> : null}
                </span>
              </button>
              <div className="cal-agenda__rows">
                {group.events.length === 0 ? <p className="cal-agenda__nada">Nada planejado</p> : null}
                {group.events.map((c) => {
                  const agenda = agendaInfo(c.agenda)
                  const concluido = Boolean(c.item?.concluido)
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className="cal-agenda__ev"
                      style={{ '--cal-ev-cor': agenda.color } as CSSProperties}
                      onClick={(event) => abrirCompromisso(c, ancoraDoClique(event))}
                    >
                      <span className={`cal-agenda__dot${concluido ? ' cal-agenda__dot--feito' : ''}`} aria-hidden />
                      <span className="cal-agenda__hora">{horarioDaLinha(c)}</span>
                      <span className="cal-agenda__texto">
                        <span className={`cal-agenda__titulo${concluido ? ' cal-agenda__titulo--feito' : ''}`}>
                          {concluido ? <i className="fas fa-check-circle" aria-hidden /> : null} {c.title}
                        </span>
                        <span className="cal-agenda__meta">
                          <i className={agenda.icon} aria-hidden /> {c.item ? c.item.code : agenda.short}
                          {c.evento?.meetingUrl || c.google?.meetingUrl ? ' · com reunião' : ''}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>
    )
  }

  const renderGradeDeHoras = () => (
    <>
      {avisoDeVazio}
      <GradeDeHoras
        dias={diasDoPeriodo(periodo.de, periodo.ate)}
        hoje={hoje}
        selecionado={selected}
        porDia={porDia}
        onAbrir={abrirCompromisso}
        onNovo={(dia, hora, ancora) => {
          setSelected(dia)
          abrirCriacao(dia, hora, ancora)
        }}
        onEscolherDia={verDia}
        foco={foco}
      />
    </>
  )

  const diaDoPop = pop?.tipo === 'dia' ? pop.dia : null
  const criacaoRapida = edicao !== null && edicao.ancora !== null

  return (
    <>
      <ExplorerBody>
        <div className="cal-side">
          <button type="button" className="cal-criar" title="Criar evento (C)" onClick={() => abrirCriacao()}>
            <i className="fas fa-plus" aria-hidden />
            Criar
          </button>

          <MiniCalendario
            selecionado={selected}
            hoje={hoje}
            semanaInteira={display === 'week'}
            onEscolher={(dia) => {
              setSelected(dia)
              setPop(null)
            }}
          />

          {/* Visão = QUEM aparece na agenda — protótipo `_shell.html:399-405` */}
          <section className="cal-side__block">
            <div className="cal-side__head">
              <span className="cal-side__label">Visão</span>
              <button
                type="button"
                className="cal-side__reset"
                disabled={!filtrosTocados}
                title="Mostrar todas as agendas e todas as pessoas"
                onClick={resetFilters}
              >
                Limpar filtros
              </button>
            </div>
            <div className="cal-side__items">
              {AUDIENCE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`cal-side__row${audience === option.id ? ' cal-side__row--on' : ''}`}
                  aria-pressed={audience === option.id}
                  onClick={() => setAudience(option.id)}
                >
                  <span className={`cal-radio${audience === option.id ? ' cal-radio--on' : ''}`} aria-hidden>
                    <span className="cal-radio__dot" />
                  </span>
                  <i className={`${option.icon} cal-side__icone`} aria-hidden />
                  <span className="cal-side__text">{option.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="cal-side__block">
            <div className="cal-side__label">Agendas</div>
            <div className="cal-side__bulk">
              <button
                type="button"
                className="cal-side__reset"
                disabled={agendasLigadas === agendasNaBarra.length}
                title="Mostrar todas as agendas"
                onClick={() => setActiveAgendas(AGENDA_IDS)}
              >
                <i className="fas fa-check-double" aria-hidden /> Marcar todas
              </button>
              <button
                type="button"
                className="cal-side__reset"
                disabled={agendasLigadas === 0}
                title="Esconder todas as agendas — depois marque só as que quiser ver"
                onClick={() => setActiveAgendas([])}
              >
                <i className="far fa-square" aria-hidden /> Desmarcar todas
              </button>
            </div>
            <div className="cal-side__items">
              {agendasNaBarra.map((agenda) => {
                const on = activeAgendas.includes(agenda.id)
                return (
                  <button
                    key={agenda.id}
                    type="button"
                    className="cal-side__row"
                    aria-pressed={on}
                    title={agenda.description}
                    style={{ '--cal-ev-cor': agenda.color } as CSSProperties}
                    onClick={() => toggleAgenda(agenda.id)}
                  >
                    <span className={`cal-check cal-check--agenda${on ? ' cal-check--on' : ''}`} aria-hidden>
                      <i className="fas fa-check" />
                    </span>
                    <i className={`${agenda.icon} cal-side__icone`} aria-hidden />
                    <span className="cal-side__text">{agenda.label}</span>
                    <span className="cal-side__hint">{on ? (porAgenda.get(agenda.id) ?? 0) : 'oculta'}</span>
                  </button>
                )
              })}
            </div>
            <p className="cal-side__note">
              <i className="fas fa-info-circle" aria-hidden /> Itens de Trabalho entram pelo início e pela entrega e
              não aceitam evento criado à mão.
            </p>
          </section>

          <section className="cal-side__block">
            <div className="cal-side__label">Google Calendar</div>
            {google.isPending ? (
              <LoadingState rows={1} label="Verificando o Google Calendar" />
            ) : google.isError ? (
              <Alert
                tone="danger"
                action={
                  <Button size="sm" icon="fas fa-redo" onClick={() => void google.refetch()}>
                    Tentar de novo
                  </Button>
                }
              >
                Não foi possível ver a conexão com o Google.
              </Alert>
            ) : !google.data.configurado ? (
              <div className="cal-google">
                <p className="cal-side__note" style={{ margin: 0 }}>
                  <i className="fas fa-info-circle" aria-hidden /> A conexão com o Google Calendar ainda não foi
                  configurada nesta instalação.
                </p>
                {session.isSuperAdmin ? (
                  <div className="cal-google__acoes">
                    <Button size="sm" icon="fas fa-cog" onClick={() => navigate('/console/google-calendar')}>
                      Configurar no Console
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : googleConectado ? (
              <div className="cal-google">
                <div className="cal-google__conta" title={google.data.email ?? undefined}>
                  <i className="fas fa-check-circle" aria-hidden style={{ color: 'var(--ob-success)' }} />
                  <span>Conectado{google.data.email ? ` como ${google.data.email}` : ''}</span>
                </div>
                {google.data.erro || consulta.data?.googleErro ? (
                  <Alert tone="warning">{consulta.data?.googleErro ?? google.data.erro}</Alert>
                ) : null}
                <div className="cal-google__acoes">
                  {google.data.erro ? (
                    <Button
                      size="sm"
                      variant="primary"
                      icon="fab fa-google"
                      loading={conectarGoogle.isPending || aguardandoGoogle}
                      onClick={() => conectarGoogle.mutate()}
                    >
                      Conectar de novo
                    </Button>
                  ) : null}
                  <Button size="sm" icon="fas fa-sync-alt" loading={consulta.isFetching} onClick={sincronizarGoogle}>
                    Sincronizar agora
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="fas fa-unlink"
                    loading={desconectarGoogle.isPending}
                    onClick={() => desconectarGoogle.mutate()}
                  >
                    Desconectar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="cal-google">
                <p className="cal-side__note" style={{ margin: 0 }}>
                  Veja aqui os eventos do seu Google Calendar e envie para lá os eventos que criar no Orbind.
                </p>
                <div className="cal-google__acoes">
                  <Button
                    size="sm"
                    variant="primary"
                    icon="fab fa-google"
                    loading={conectarGoogle.isPending || aguardandoGoogle}
                    onClick={() => conectarGoogle.mutate()}
                  >
                    {aguardandoGoogle ? 'Aguardando o Google…' : 'Conectar Google'}
                  </Button>
                  {aguardandoGoogle ? (
                    <Button size="sm" variant="ghost" onClick={() => setAguardandoGoogle(false)}>
                      Cancelar
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
          </section>

          <section className="cal-side__block">
            <div className="cal-side__head">
              <span className="cal-side__label">Pessoas</span>
              <span className="cal-side__hint">{dicaPessoas}</span>
            </div>
            {bootstrap.isPending ? (
              <LoadingState rows={4} label="Carregando as pessoas" />
            ) : bootstrap.isError ? (
              <Alert
                tone="danger"
                action={
                  <Button size="sm" icon="fas fa-redo" onClick={() => void bootstrap.refetch()}>
                    Tentar de novo
                  </Button>
                }
              >
                Não foi possível carregar as pessoas.
              </Alert>
            ) : pessoas.length === 0 ? (
              <p className="cal-side__note">Nenhuma pessoa ativa no workspace.</p>
            ) : (
              <>
                <div className="cal-side__bulk">
                  <button
                    type="button"
                    className="cal-side__reset"
                    disabled={audience === 'all'}
                    title="Mostrar a agenda de todas as pessoas"
                    onClick={marcarTodasAsPessoas}
                  >
                    <i className="fas fa-check-double" aria-hidden /> Marcar todos
                  </button>
                  <button
                    type="button"
                    className="cal-side__reset"
                    disabled={audience === 'custom' && marcadas === 0}
                    title="Desmarcar todas as pessoas — ficam só Corporativo e Datas comemorativas"
                    onClick={desmarcarTodasAsPessoas}
                  >
                    <i className="far fa-square" aria-hidden /> Desmarcar todos
                  </button>
                </div>
                <div className="cal-side__items">
                  {pessoas.map((person) => {
                    const on = audience === 'all' || (audience === 'custom' && activePeople.includes(person.id))
                    return (
                      <button
                        key={person.id}
                        type="button"
                        className={`cal-side__row${audience === 'me' ? ' cal-side__row--off' : ''}`}
                        aria-pressed={on}
                        onClick={() => togglePerson(person.id)}
                      >
                        <span className={`cal-check${on ? ' cal-check--on' : ''}`} aria-hidden>
                          <i className="fas fa-check" />
                        </span>
                        <span className="wi-avatar" aria-hidden>
                          {initialsOf(person.name)}
                        </span>
                        <span className="cal-side__text">{person.name}</span>
                        {person.id === eu ? <span className="cal-side__hint">você</span> : null}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      </ExplorerBody>

      <div className="cal">
        {/* barra do design: Hoje · ‹ › · período · painel do dia · exibição */}
        <header className="cal-bar">
          <button type="button" className="cal-pill" title="Ir para hoje (T)" onClick={irParaHoje}>
            <i className="fas fa-calendar-day" aria-hidden /> Hoje
          </button>
          <div className="cal-bar__setas">
            <button
              type="button"
              className="cal-round cal-round--lg"
              aria-label={NAV_LABEL[display][0]}
              title={`${NAV_LABEL[display][0]} (K)`}
              onClick={() => irPara(-1)}
            >
              <i className="fas fa-chevron-left" aria-hidden />
            </button>
            <button
              type="button"
              className="cal-round cal-round--lg"
              aria-label={NAV_LABEL[display][1]}
              title={`${NAV_LABEL[display][1]} (J)`}
              onClick={() => irPara(1)}
            >
              <i className="fas fa-chevron-right" aria-hidden />
            </button>
          </div>
          <h1 className="cal-bar__title">{titulo}</h1>
          {atualizando ? (
            <span className="cal-bar__sync" role="status">
              <i className="fas fa-circle-notch fa-spin" aria-hidden /> atualizando
            </span>
          ) : null}
          <span className="ob-grow" />
          <button
            type="button"
            className={`cal-round cal-round--lg${painelAberto ? ' cal-round--on' : ''}`}
            aria-pressed={painelAberto}
            aria-label={painelAberto ? 'Recolher painel do dia' : 'Mostrar painel do dia'}
            title={`${painelAberto ? 'Recolher' : 'Mostrar'} painel do dia ( ] )`}
            onClick={() => setPainelAberto(!painelAberto)}
          >
            <i className="fas fa-columns" aria-hidden />
          </button>
          <div className="cal-views" role="group" aria-label="Exibição do calendário">
            {EXIBICOES.map((opcao) => (
              <button
                key={opcao.id}
                type="button"
                className={display === opcao.id ? 'is-on' : undefined}
                aria-pressed={display === opcao.id}
                title={`${opcao.label} (${opcao.atalho})`}
                onClick={() => setDisplay(opcao.id)}
              >
                {opcao.label}
              </button>
            ))}
          </div>
        </header>

        <div className="cal__body" ref={corpoRef}>
          <main className="cal-card">
            {consulta.isPending ? (
              <div className="cal__loading">
                <LoadingState rows={6} label="Carregando o calendário" />
              </div>
            ) : consulta.isError ? (
              <div className="cal__loading">
                <Alert
                  tone="danger"
                  title="Não foi possível carregar o calendário"
                  action={
                    <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
                      Tentar de novo
                    </Button>
                  }
                >
                  {erroDe(consulta.error)}
                </Alert>
              </div>
            ) : display === 'month' ? (
              renderMes()
            ) : display === 'agenda' ? (
              renderAgenda()
            ) : (
              renderGradeDeHoras()
            )}
          </main>

          {painelAberto ? (
            <>
              {/* arraste para ajustar a largura do painel do dia; duplo clique volta ao padrão */}
              <div
                className="cal__divisor"
                role="separator"
                aria-orientation="vertical"
                aria-label="Ajustar a largura do painel do dia"
                aria-valuemin={LARGURA_DO_DIA_MIN}
                aria-valuemax={LARGURA_DO_DIA_MAX}
                aria-valuenow={larguraDoDia}
                tabIndex={0}
                title="Arraste para ajustar a largura (duplo clique volta ao padrão)"
                onPointerDown={iniciarArrasteDoDia}
                onKeyDown={teclaNoDivisor}
                onDoubleClick={() => setLarguraDoDia(LARGURA_DO_DIA_PADRAO)}
              />

              <aside
                className="cal-day"
                aria-label="Dia selecionado"
                style={{ '--cal-day-largura': `${larguraDoDia}px` } as CSSProperties}
              >
                <div className="cal-day__head">
                  <div className="cal-day__id">
                    <h2 className="cal-day__title">{capitalize(DAY_LONG_FMT.format(parseDayKey(selected)))}</h2>
                    <span className="cal-day__sub">
                      Meus compromissos · {meusNoDia.length === 0 ? 'dia livre' : plural(meusNoDia.length)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="cal-day__hoje"
                    title="Voltar para hoje"
                    onClick={() => {
                      setSelected(hoje)
                      setFocoDoDia((n) => n + 1)
                    }}
                  >
                    Hoje
                  </button>
                  <button
                    type="button"
                    className="cal-round cal-round--md"
                    aria-label="Novo evento neste dia"
                    title="Novo evento neste dia"
                    onClick={() => abrirCriacao(selected)}
                  >
                    <i className="fas fa-plus" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="cal-round cal-round--md"
                    aria-label="Dia anterior"
                    title="Dia anterior"
                    onClick={() => setSelected(somarDias(selected, -1))}
                  >
                    <i className="fas fa-chevron-left" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="cal-round cal-round--md"
                    aria-label="Próximo dia"
                    title="Próximo dia"
                    onClick={() => setSelected(somarDias(selected, 1))}
                  >
                    <i className="fas fa-chevron-right" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="cal-round cal-round--md"
                    aria-label="Recolher painel do dia"
                    title="Recolher painel ( ] )"
                    onClick={() => setPainelAberto(false)}
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                </div>

                {/* O dia em faixas de horário, como o painel do Google na lateral do
                    Gmail (SPEC-20261104-1428): só os compromissos de QUEM está
                    logado; clicar numa hora vazia cria, clicar num bloco abre. */}
                <div className="cal-day__grade">
                  {consulta.isPending ? (
                    <LoadingState rows={3} label="Carregando o dia" />
                  ) : (
                    <GradeDeHoras
                      dias={[selected]}
                      hoje={hoje}
                      selecionado={selected}
                      porDia={porDiaMeus}
                      onAbrir={abrirCompromisso}
                      onNovo={(dia, hora, ancora) => abrirCriacao(dia, hora, ancora)}
                      onEscolherDia={setSelected}
                      foco={focoDoDia}
                    />
                  )}
                </div>
              </aside>
            </>
          ) : null}
        </div>
      </div>

      {/* ------------------------------------------------ popovers ------ */}
      {pop?.tipo === 'compromisso' ? (
        <Popover ancora={pop.ancora} largura={400} rotulo={pop.c.title} onFechar={() => setPop(null)}>
          <DetalheDoCompromisso
            c={pop.c}
            hoje={hoje}
            nomeDe={nomeDe}
            emailDoGoogle={google.data?.email ?? null}
            onFechar={() => setPop(null)}
            onEditar={abrirEdicao}
            onExcluir={(ev) => {
              setPop(null)
              setExcluindo(ev)
            }}
            onAbrirItem={(id) => navigate(`/kanbans/items/${id}`)}
            onAbrirLink={(url) => void abrirNoNavegadorPadrao(url)}
          />
        </Popover>
      ) : null}

      {diaDoPop && pop ? (
        <Popover
          ancora={pop.ancora}
          largura={280}
          rotulo={capitalize(DAY_LONG_FMT.format(parseDayKey(diaDoPop)))}
          onFechar={() => setPop(null)}
        >
          <div className="cal-daypop">
            <div className="cal-daypop__topo">
              <button
                type="button"
                className="cal-round cal-round--md"
                aria-label="Fechar (Esc)"
                title="Fechar (Esc)"
                onClick={() => setPop(null)}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
            <div className="cal-daypop__cabeca">
              <span className="cal-daypop__dow">{WEEKDAYS[parseDayKey(diaDoPop).getDay()]}</span>
              <button type="button" className="cal-daypop__num" title="Ver dia" onClick={() => verDia(diaDoPop)}>
                {parseDayKey(diaDoPop).getDate()}
              </button>
            </div>
            <div className="cal-daypop__lista">
              {(porDia.get(diaDoPop) ?? []).map((c) => (
                <Chip key={c.id} c={c} dia={diaDoPop} onAbrir={abrirCompromisso} />
              ))}
            </div>
          </div>
        </Popover>
      ) : null}

      {criacaoRapida && edicao?.ancora ? (
        <Popover ancora={edicao.ancora} largura={440} rotulo="Novo evento" onFechar={() => setEdicao(null)}>
          <CriacaoRapida
            form={edicao.form}
            erros={erros}
            tentou={tentouSalvar}
            googleConectado={googleConectado}
            salvando={salvar.isPending}
            onMudar={atualizarForm}
            onSalvar={salvarEvento}
            onMaisOpcoes={() => setEdicao({ ...edicao, ancora: null })}
            onFechar={() => setEdicao(null)}
          />
        </Popover>
      ) : null}

      <ConfirmDialog
        open={excluindo !== null}
        title="Excluir evento"
        description={
          excluindo
            ? `“${excluindo.title}” sai do calendário de todos${excluindo.memberIds.length > 0 ? ', inclusive dos convidados' : ''}${excluindo.googleLink ? ' e do seu Google Calendar' : ''}. Não dá para desfazer.`
            : ''
        }
        confirmLabel="Excluir"
        destructive
        loading={excluir.isPending}
        onConfirm={() => excluindo && excluir.mutate(excluindo.id)}
        onCancel={() => setExcluindo(null)}
      />

      {/* --------------------------------------- Novo / Editar evento -- */}
      <Modal
        open={edicao !== null && !criacaoRapida}
        onClose={() => setEdicao(null)}
        title={edicao?.id ? 'Editar evento' : 'Novo evento'}
        width={540}
        footer={
          <>
            <Button onClick={() => setEdicao(null)}>Cancelar</Button>
            <Button
              variant="primary"
              icon="fas fa-check"
              disabled={!sujo || salvar.isPending}
              title={sujo ? undefined : 'Nada mudou'}
              onClick={salvarEvento}
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        {edicao ? (
          <div className="cal-new">
            <FieldBlock label="Título" required error={tentouSalvar ? erros.title : null}>
              {({ id, invalid }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  value={edicao.form.title}
                  placeholder="Adicionar título"
                  maxLength={300}
                  onChange={(event) => atualizarForm({ title: event.target.value })}
                />
              )}
            </FieldBlock>

            <div className="cal-new__grid">
              <FieldBlock label="Agenda" required>
                {({ id }) => (
                  <Select
                    id={id}
                    value={edicao.form.agenda}
                    options={OPCOES_DE_AGENDA}
                    onChange={(agenda) => atualizarForm({ agenda })}
                  />
                )}
              </FieldBlock>
              <FieldBlock label="Data" required error={tentouSalvar ? erros.date : null}>
                {({ id, invalid }) => (
                  <DateInput
                    id={id}
                    invalid={invalid}
                    value={edicao.form.date}
                    clearable={false}
                    onChange={(date) => atualizarForm({ date })}
                  />
                )}
              </FieldBlock>
            </div>

            <Checkbox
              checked={edicao.form.diaInteiro}
              label="Dia inteiro"
              onChange={(event) => {
                const diaInteiro = event.target.checked
                // ao ganhar hora, já vem um horário comum preenchido
                atualizarForm(
                  !diaInteiro && edicao.form.startTime === ''
                    ? { diaInteiro, startTime: '09:00', endTime: '10:00' }
                    : { diaInteiro },
                )
              }}
            />

            {edicao.form.diaInteiro ? null : (
              <div className="cal-new__grid">
                <FieldBlock label="Hora de início" required error={tentouSalvar ? erros.startTime : null}>
                  {({ id, invalid }) => (
                    <Input
                      id={id}
                      invalid={invalid}
                      value={edicao.form.startTime}
                      placeholder="09:00"
                      inputMode="numeric"
                      maxLength={5}
                      onChange={(event) => atualizarForm({ startTime: event.target.value })}
                    />
                  )}
                </FieldBlock>
                <FieldBlock label="Hora de término" hint="Em branco: 1 hora." error={tentouSalvar ? erros.endTime : null}>
                  {({ id, invalid }) => (
                    <Input
                      id={id}
                      invalid={invalid}
                      value={edicao.form.endTime}
                      placeholder="10:00"
                      inputMode="numeric"
                      maxLength={5}
                      onChange={(event) => atualizarForm({ endTime: event.target.value })}
                    />
                  )}
                </FieldBlock>
              </div>
            )}

            <FieldBlock
              label="Término em outro dia"
              hint="Opcional — para eventos de vários dias."
              error={tentouSalvar ? erros.endDate : null}
            >
              {({ id, invalid }) => (
                <DateInput
                  id={id}
                  invalid={invalid}
                  value={edicao.form.endDate}
                  placeholder="No mesmo dia"
                  onChange={(endDate) => atualizarForm({ endDate })}
                />
              )}
            </FieldBlock>

            <FieldBlock label="Convidados" hint="Quem você convidar recebe um aviso e vê o evento na agenda.">
              {({ id }) => (
                <TagSelect
                  id={id}
                  value={edicao.form.memberIds}
                  options={opcoesDeConvidado}
                  placeholder="Digite um nome…"
                  emptyLabel="Nenhuma pessoa encontrada"
                  onChange={(memberIds) => atualizarForm({ memberIds })}
                />
              )}
            </FieldBlock>

            <div className="cal-new__grid">
              <FieldBlock label="Local">
                {({ id }) => (
                  <Input
                    id={id}
                    value={edicao.form.location}
                    placeholder="Sala, endereço…"
                    maxLength={300}
                    onChange={(event) => atualizarForm({ location: event.target.value })}
                  />
                )}
              </FieldBlock>
              <FieldBlock label="Link da reunião" error={tentouSalvar ? erros.meetingUrl : null}>
                {({ id, invalid }) => (
                  <Input
                    id={id}
                    invalid={invalid}
                    value={edicao.form.meetingUrl}
                    placeholder="https://…"
                    inputMode="url"
                    maxLength={1000}
                    onChange={(event) => atualizarForm({ meetingUrl: event.target.value })}
                  />
                )}
              </FieldBlock>
            </div>

            <FieldBlock label="Descrição">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={edicao.form.description}
                  placeholder="Pauta, observações…"
                  maxLength={10_000}
                  onChange={(event) => atualizarForm({ description: event.target.value })}
                />
              )}
            </FieldBlock>

            {/* só quem criou envia para o PRÓPRIO Google (SPEC-20261027-2350-integracao-google-calendar) */}
            {edicao.dono === eu ? (
              <div>
                <Checkbox
                  checked={edicao.form.googleSync && googleConectado}
                  disabled={!googleConectado}
                  label="Enviar também para o meu Google Calendar"
                  onChange={(event) => atualizarForm({ googleSync: event.target.checked })}
                />
                {!googleConectado ? (
                  <p className="cal-side__note">
                    <i className="fab fa-google" aria-hidden /> Conecte o Google Calendar no painel do Calendário para usar
                    esta opção.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  )
}

/* ------------------------------------------------------ painel do dia ---- */

/**
 * Cartão do painel do dia (design): hora, agenda (ícone + nome, na cor dela),
 * "Concluído" quando for o caso; o título; e o vínculo — o código do Item de
 * Trabalho com o responsável, ou a reunião.
 */
function CartaoDoDia({
  c,
  nomeDe,
  onAbrir,
}: {
  c: Compromisso
  nomeDe: (id: string) => string
  onAbrir: (c: Compromisso, ancora: Ancora) => void
}) {
  const agenda = agendaInfo(c.agenda)
  const concluido = Boolean(c.item?.concluido)
  const reuniao = c.evento?.meetingUrl ?? c.google?.meetingUrl ?? null
  return (
    <button
      type="button"
      className="cal-cartao"
      style={{ '--cal-ev-cor': agenda.color } as CSSProperties}
      onClick={(event) => onAbrir(c, ancoraDoClique(event))}
    >
      <span className="cal-cartao__topo">
        <span>{horarioDaLinha(c)}</span>
        <span className="cal-cartao__agenda">
          <i className={agenda.icon} aria-hidden /> {agenda.short}
        </span>
        {concluido ? (
          <span className="cal-cartao__feito">
            <i className="fas fa-check-circle" aria-hidden /> Concluído
          </span>
        ) : null}
      </span>
      <span className={`cal-cartao__titulo${concluido ? ' cal-cartao__titulo--feito' : ''}`}>{c.title}</span>
      {c.item ? (
        <span className="cal-cartao__wi">
          <i className="fas fa-link" aria-hidden /> {c.item.code}
          {c.item.assigneeMemberId ? <span className="cal-cartao__dono">· {nomeDe(c.item.assigneeMemberId)}</span> : null}
        </span>
      ) : null}
      {c.location ? (
        <span className="cal-cartao__extra">
          <i className="fas fa-map-marker-alt" aria-hidden /> {c.location}
        </span>
      ) : null}
      {reuniao ? (
        <span className="cal-cartao__extra">
          <i className="fas fa-video" aria-hidden /> {/meet\.google\.com/i.test(reuniao) ? 'Google Meet' : 'Com link de reunião'}
        </span>
      ) : null}
    </button>
  )
}
